/**
 * SchedulerTraceContext
 * ──────────────────────────────────────────────────────────────────────
 * Single source of truth for the `data-scheduler-trace` attribute on
 * the <EchoScheduler> root. Each tab calls `useReportSchedulerSource()`
 * inside a `useEffect` whenever its `data-*-source` state changes.
 *
 * The root reads `useSchedulerTraceString()` to render a stable,
 * pipe-delimited trace string:
 *
 *     today:live|calendar:live|rooming:api_missing|waitlist:live|
 *     recall:loading|insights:live|settings:live
 *
 * This is intentionally PIPE format (not JSON) — selector-safe in
 * CSS/Playwright, grep-friendly in production traces, and stable
 * across React re-renders. Each per-tab `data-*-source` remains the
 * source of truth on the tab's own root; this attribute just rolls
 * them up.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const TAB_ORDER = ['today', 'calendar', 'rooming', 'waitlist', 'recall', 'insights', 'settings'];

const SchedulerTraceCtx = createContext({
  report: () => {},
  trace: '',
  states: {},
});

export const SchedulerTraceProvider = ({ children }) => {
  const [states, setStates] = useState({});

  const report = useCallback((tab, source) => {
    setStates((prev) => {
      if (prev[tab] === source) return prev;
      return { ...prev, [tab]: source };
    });
  }, []);

  const trace = useMemo(
    () => TAB_ORDER
      .filter((t) => states[t] !== undefined && states[t] !== null)
      .map((t) => `${t}:${states[t]}`)
      .join('|'),
    [states],
  );

  const value = useMemo(() => ({ report, trace, states }), [report, trace, states]);

  return (
    <SchedulerTraceCtx.Provider value={value}>
      {children}
    </SchedulerTraceCtx.Provider>
  );
};

/** Read the rolled-up trace string for the root attribute. */
export const useSchedulerTraceString = () => useContext(SchedulerTraceCtx).trace;

/** Read the raw per-tab states map (for telemetry export / capture JSON). */
export const useSchedulerTraceStates = () => useContext(SchedulerTraceCtx).states;

/** Tabs call this inside useEffect to register / update their source state. */
export const useReportSchedulerSource = (tab, source) => {
  const { report } = useContext(SchedulerTraceCtx);
  useEffect(() => {
    if (!tab || source === undefined || source === null) return;
    report(tab, source);
  }, [tab, source, report]);
};
